import React, { useState, useEffect } from 'react';
import { getLeagueMatches } from '../../services/leagueService';
import CreateMatchModal from './CreateMatchModal';
import ScoreEntryModal from './ScoreEntryModal';
import BracketView from './BracketView';
import GenerateScheduleModal from './GenerateScheduleModal';
import { IconPlus, IconEdit, IconCalendar, IconList, IconTournament, IconArrowNarrowRight } from '@tabler/icons-react';

const MatchesTab = ({ leagueId, league, members, isAdmin }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'bracket'

  // Determine if this league should have bracket view
  const isTournamentFormat = league?.format?.includes('elimination');
  const canGenerateSchedule = (league?.format === 'round-robin' || isTournamentFormat) && members.length >= 2;

  useEffect(() => {
    loadMatches();
  }, [leagueId]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const matchesData = await getLeagueMatches(leagueId);
      setMatches(matchesData);
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMatchCreated = () => {
    setShowCreateModal(false);
    loadMatches();
  };

  const handleScoreUpdated = () => {
    setSelectedMatch(null);
    loadMatches();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: { label: 'Scheduled', class: 'scheduled' },
      in_progress: { label: 'In Progress', class: 'in-progress' },
      completed: { label: 'Completed', class: 'completed' }
    };
    return badges[status] || badges.scheduled;
  };

  const filteredMatches = matches.filter(match => {
    if (filterStatus !== 'all' && match.status !== filterStatus) return false;
    return true;
  });

  return (
    <section className="ls-matches-tab">
      <header className="ls-matches-header">
        <div className="ls-matches-header-left">
          <h2>Matches ({matches.length})</h2>
          {isTournamentFormat && matches.length > 0 && (
            <div className="ls-view-toggle">
              <button
                className={`ls-view-toggle-btn ${viewMode === 'list' ? 'ls-active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <IconList size={18} />
                <span>List</span>
              </button>
              <button
                className={`ls-view-toggle-btn ${viewMode === 'bracket' ? 'ls-active' : ''}`}
                onClick={() => setViewMode('bracket')}
              >
                <IconTournament size={18} />
                <span>Bracket</span>
              </button>
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="ls-matches-header-actions">
            {canGenerateSchedule && matches.length === 0 && (
              <button
                className="ls-btn-generate-schedule"
                onClick={() => setShowGenerateModal(true)}
              >
                <IconArrowNarrowRight size={20} />
                <span>Generate Schedule</span>
              </button>
            )}
            <button
              className="ls-btn-create-match"
              onClick={() => setShowCreateModal(true)}
            >
              <IconPlus size={20} />
              <span>Add Match</span>
            </button>
          </div>
        )}
      </header>

      {matches.length > 0 && !isTournamentFormat && (
        <div className="ls-matches-filters">
          <div className="ls-filter-group">
            <label htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="ls-loading-matches">
          <p>Loading matches...</p>
        </div>
      ) : filteredMatches.length === 0 ? (
        <div className="ls-empty-matches">
          <p>
            {matches.length === 0
              ? 'No matches scheduled yet. Admins can create matches to get started.'
              : 'No matches match the current filters.'}
          </p>
        </div>
      ) : viewMode === 'bracket' && isTournamentFormat ? (
        <BracketView
          matches={filteredMatches}
          format={league?.format}
          isAdmin={isAdmin}
          onMatchClick={(match) => setSelectedMatch(match)}
        />
      ) : (
        <div className="ls-matches-list">
          {filteredMatches.map(match => {
            const statusBadge = getStatusBadge(match.status);
            return (
              <article key={match.id} className="ls-match-card">
                <div className="ls-match-header-info">
                  <div className="ls-match-status-group">
                    <span className={`ls-match-status ${statusBadge.class}`}>
                      {statusBadge.label}
                    </span>
                    {match.status === 'completed' && match.updatedAt && (
                      <span className="ls-match-date">
                        {new Date(match.updatedAt.seconds * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="ls-match-participants">
                  <div className={`ls-participant ${match.winnerId === match.participant1Id ? 'ls-winner' : ''}`}>
                    <span className="ls-participant-name">{match.participant1Name}</span>
                    <span className="ls-participant-score">
                      {match.status === 'completed' ? match.participant1Score : '-'}
                    </span>
                  </div>

                  <div className="ls-vs-separator">VS</div>

                  <div className={`ls-participant ${match.winnerId === match.participant2Id ? 'ls-winner' : ''}`}>
                    <span className="ls-participant-name">{match.participant2Name}</span>
                    <span className="ls-participant-score">
                      {match.status === 'completed' ? match.participant2Score : '-'}
                    </span>
                  </div>
                </div>

                <div className="ls-match-details">
                  {match.scheduledTime && (
                    <div className="ls-match-detail-item">
                      <IconCalendar size={16} />
                      <span>{formatDateTime(match.scheduledTime)}</span>
                    </div>
                  )}
                  {match.location && (
                    <div className="ls-match-detail-item">
                      <span>{match.location}</span>
                    </div>
                  )}
                </div>

                {isAdmin && match.status !== 'completed' && (
                  <button
                    className="ls-btn-enter-score"
                    onClick={() => setSelectedMatch(match)}
                  >
                    <IconEdit size={16} />
                    <span>Enter Score</span>
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateMatchModal
          leagueId={leagueId}
          members={members}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleMatchCreated}
        />
      )}

      {showGenerateModal && (
        <GenerateScheduleModal
          leagueId={leagueId}
          league={league}
          members={members}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={handleMatchCreated}
        />
      )}

      {selectedMatch && (
        <ScoreEntryModal
          leagueId={leagueId}
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onSuccess={handleScoreUpdated}
        />
      )}
    </section>
  );
};

export default MatchesTab;
